import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarCheck, ClipboardList, CreditCard, BarChart3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SiteLayout } from "@/components/site/Layout";
import { ClinicCard } from "@/components/site/ClinicCard";
import { UpcomingFeed } from "@/components/site/UpcomingFeed";
import { api } from "@/lib/api";
import { useContent, useSeo } from "@/context/ContentContext";

const HERO_A = "https://images.unsplash.com/photo-1649751361457-01d3a696c7e6?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";
const HERO_B = "https://images.unsplash.com/photo-1759987383760-327efaf5522a?crop=entropy&cs=srgb&fm=jpg&q=85&w=600";

const Headline = ({ text = "", hl = "" }) => {
  const i = hl ? text.indexOf(hl) : -1;
  if (i < 0) return text;
  return <>{text.slice(0, i)}<span className="text-primary">{hl}</span>{text.slice(i + hl.length)}</>;
};

const Hero = ({ h }) => (
  <section className="relative overflow-hidden grain">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-20 grid lg:grid-cols-[1.1fr_1fr] gap-12 items-center">
      <div className="space-y-7 fade-up">
        <p className="eyebrow" data-testid="hero-eyebrow">{h.hero_eyebrow}</p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.02]" data-testid="hero-title">
          <Headline text={h.hero_title} hl={h.hero_highlight} />
        </h1>
        <p className="text-base sm:text-lg text-slate-600 max-w-xl" data-testid="hero-subtitle">
          {h.hero_subtitle}
        </p>
        <div className="flex flex-wrap gap-3">
          <Button size="lg" className="rounded-full px-7 h-12" asChild data-testid="hero-find-clinic-button">
            <Link to="/clinics">{h.cta_primary} <ArrowRight className="h-4 w-4 ml-2" /></Link>
          </Button>
          <Button size="lg" variant="outline" className="rounded-full px-7 h-12 bg-card" asChild data-testid="hero-list-clinic-button">
            <Link to="/for-clinics/signup">{h.cta_secondary}</Link>
          </Button>
        </div>
      </div>
      <div className="relative h-[360px] sm:h-[440px] fade-up" style={{ animationDelay: "120ms" }}>
        <img src={h.hero_image_a || HERO_A} alt="Physiotherapist treating a patient" className="absolute right-0 top-0 w-[78%] h-[78%] object-cover rounded-[2rem] shadow-xl" />
        <img src={h.hero_image_b || HERO_B} alt="Bright clinic waiting room" className="absolute left-0 bottom-0 w-[50%] h-[48%] object-cover rounded-[1.5rem] border-[6px] border-background shadow-xl" />
        <div className="absolute left-4 top-10 rounded-2xl bg-card/90 backdrop-blur-xl border border-border px-4 py-3 shadow-lg hidden sm:block">
          <p className="text-xs text-muted-foreground">Book online</p>
          <p className="font-display font-bold text-sm">Any time, 24/7</p>
        </div>
      </div>
    </div>
  </section>
);

const Categories = ({ items, h }) => (
  <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
    <p className="eyebrow">{h.categories_eyebrow}</p>
    <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mt-2 mb-8">{h.categories_title}</h2>
    <div className="grid grid-cols-2 lg:grid-cols-12 gap-4">
      {items.map((d, i) => (
        <Link key={d.slug} to={`/clinics?discipline=${d.slug}`} data-testid={`category-tile-item-${d.slug}`}
          className={`group relative overflow-hidden rounded-2xl lift ${i < 2 ? "lg:col-span-6 aspect-[4/3] lg:aspect-[16/8]" : "lg:col-span-3 aspect-square"}`}>
          <img src={d.image} alt={d.name} className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1E3A34]/85 via-[#1E3A34]/20 to-transparent" />
          <div className="absolute bottom-0 p-4 text-white">
            <p className="font-display font-bold text-lg">{d.name}</p>
            <p className="text-xs text-white/80">{d.clinic_count} clinic{d.clinic_count === 1 ? "" : "s"}</p>
          </div>
        </Link>
      ))}
    </div>
  </section>
);

const FEATURES = [
  { icon: CalendarCheck, t: "Online booking", d: "Weekly availability per practitioner, live open slots." },
  { icon: ClipboardList, t: "Intake forms", d: "Health questions collected before every first visit." },
  { icon: CreditCard, t: "Paid at booking", d: "Card payments taken upfront to cut no-shows." },
  { icon: BarChart3, t: "Reports", d: "Revenue and bookings by treatment and practitioner." },
];

const ForClinicsBand = ({ h }) => (
  <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
    <div className="rounded-[2rem] bg-[#1E3A34] text-white p-8 sm:p-12 grid lg:grid-cols-[1fr_1.3fr] gap-10 relative overflow-hidden grain">
      <div className="space-y-5">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">{h.band_eyebrow}</p>
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">{h.band_title}</h2>
        <p className="text-slate-300">{h.band_text}</p>
        <Button className="rounded-full bg-accent hover:bg-accent/90 px-6" asChild data-testid="band-list-clinic-button">
          <Link to="/for-clinics/signup">List your clinic</Link>
        </Button>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        {FEATURES.map(({ icon: I, t, d }) => (
          <div key={t} className="rounded-2xl bg-white/5 border border-white/10 p-5">
            <I className="h-5 w-5 text-accent mb-3" />
            <p className="font-semibold">{t}</p>
            <p className="text-sm text-slate-300 mt-1">{d}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export default function Home() {
  const { home: h } = useContent();
  useSeo("home");
  const [disciplines, setDisciplines] = useState([]);
  const [data, setData] = useState({ popular: [], upcoming: [] });
  useEffect(() => {
    api.get("/disciplines").then((r) => setDisciplines(r.data));
    api.get("/home").then((r) => setData(r.data));
  }, []);

  return (
    <SiteLayout>
      <Hero h={h} />
      <Categories items={disciplines} h={h} />
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <p className="eyebrow">{h.popular_eyebrow}</p>
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mt-2 mb-8">{h.popular_title}</h2>
        <div className="flex gap-5 overflow-x-auto pb-4 -mx-4 px-4 snap-x" data-testid="popular-clinics-row">
          {data.popular.map((c) => <div key={c.id} className="snap-start"><ClinicCard c={c} compact /></div>)}
        </div>
      </section>
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="upcoming-section">
        <p className="eyebrow">{h.upcoming_eyebrow}</p>
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mt-2">{h.upcoming_title}</h2>
        <p className="text-muted-foreground mt-2 mb-8">{h.upcoming_subtitle}</p>
        <UpcomingFeed items={data.upcoming} disciplines={disciplines} />
        <div className="mt-10">
          <Button variant="outline" className="rounded-full bg-card" asChild data-testid="browse-all-clinics-button">
            <Link to="/clinics">Browse all clinics <ArrowRight className="h-4 w-4 ml-2" /></Link>
          </Button>
        </div>
      </section>
      <ForClinicsBand h={h} />
    </SiteLayout>
  );
}
